import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/ui/Toast';
import { useCampaign, useUpdateCampaign, type TriggerConfig } from '../../api/campaigns.api';
import { useJobs } from '../../api/jobs.api';
import { useJobStages, usePipelines } from '../../api/trigger-options.api';
import { useAllAvailableLists } from '../../api/audiences.api';
import { EMPLOYMENT_TYPE_OPTIONS, placementOptionValue } from '../../lib/trigger-options';
import { MeetingBookedFields, type MeetingPersonType } from './MeetingBookedFields';
import {
  FormCard,
  FormHeader,
  WizardLayout,
  WizardFooter,
  stepInfo,
  stepPath,
  type EnrollmentMode,
} from './builder-shell';

/* ── Campaign builder · Enrollment step (Audience vs Trigger) ──
 *
 * Sits between Basics and Audience. The user chooses HOW contacts enter the
 * campaign:
 *   • Set by audience → enrol a static snapshot at launch (classic flow). The
 *     next step is the Audience picker.
 *   • Set by trigger  → auto-enrol contacts as JT events fire. The Audience
 *     step is skipped; the next step is the Sequence builder.
 *
 * The choice is persisted via Campaign.trigger_config (null = audience mode).
 * Trigger options read from Engage caches refreshed by jt-sync — dropdowns are
 * empty until a sync has run.
 */

// The trigger kinds surfaced in the builder. The legacy candidate-`type`
// `stage_change` kind is intentionally omitted (it isn't a real pipeline stage).
type UiTriggerKind = 'application_stage_change' | 'deal_stage_change' | 'meeting_booked' | 'list_added' | 'new_placement';

const TRIGGER_TYPES: { id: UiTriggerKind; label: string; description: string; icon: ReactNode }[] = [
  {
    id: 'application_stage_change',
    label: 'Job pipeline stage',
    description: "Enrol a candidate when their application moves to a stage on a job.",
    icon: <IconFlow />,
  },
  {
    id: 'deal_stage_change',
    label: 'Deal pipeline stage',
    description: 'Enrol a deal card’s contact(s) when it moves into a stage on a pipeline.',
    icon: <IconColumns />,
  },
  {
    id: 'meeting_booked',
    label: 'Meeting booked',
    description: 'Enrol the person who books a meeting through a JobTetris booking link.',
    icon: <IconCalendar />,
  },
  {
    id: 'list_added',
    label: 'Added to a list',
    description: 'Enrol a contact the moment they land on a specific list.',
    icon: <IconList />,
  },
  {
    id: 'new_placement',
    label: 'New placement',
    description: 'Enrol a candidate when a placement is created, optionally by type.',
    icon: <IconBriefcase />,
  },
];

export function CampaignTriggersPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: campaign, isLoading } = useCampaign(id);
  const updateCampaign = useUpdateCampaign(id ?? '');

  const [mode, setMode] = useState<EnrollmentMode>('audience');
  const [triggerType, setTriggerType] = useState<UiTriggerKind>('application_stage_change');
  // Timing for the stage triggers: enrol on entering the stage ('change') or
  // after it's sat there for N days ('dwell'). Shared by the deal + job cards.
  const [stageMode, setStageMode] = useState<'change' | 'dwell'>('change');
  const [dwellDays, setDwellDays] = useState(3);
  // application_stage_change
  const [jobId, setJobId] = useState('');
  const [appToStage, setAppToStage] = useState('');
  const [appFromStage, setAppFromStage] = useState('');
  // deal_stage_change
  const [pipelineId, setPipelineId] = useState('');
  const [dealToStageId, setDealToStageId] = useState('');
  const [dealFromStageId, setDealFromStageId] = useState('');
  // list_added
  const [listId, setListId] = useState('');
  const lists = useAllAvailableLists();
  // A saved list that's no longer in JobTetris still shows, so the choice isn't silently lost.
  const listOptions = useMemo(() => {
    const options = lists.map((l) => ({ value: l.id, label: `${l.name} (${l.entity_type})` }));
    if (listId && !lists.some((l) => l.id === listId)) options.unshift({ value: listId, label: 'A list no longer in JobTetris' });
    return options;
  }, [lists, listId]);
  // new_placement
  const [employmentType, setEmploymentType] = useState('');
  // meeting_booked (JT1-1920)
  const [meetingLinkIds, setMeetingLinkIds] = useState<string[]>([]);
  const [meetingPersonType, setMeetingPersonType] = useState<MeetingPersonType>('any');

  // Seed local state from the persisted trigger_config so re-visiting the step
  // shows the previously-saved choice.
  useEffect(() => {
    const tc = campaign?.trigger_config;
    if (!tc) {
      setMode('audience');
      return;
    }
    setMode('trigger');
    const m = tc.match ?? {};
    if (tc.type === 'deal_stage_change' || tc.type === 'deal_stage_dwell') {
      setTriggerType('deal_stage_change');
      setStageMode(tc.type === 'deal_stage_dwell' ? 'dwell' : 'change');
      setPipelineId(m.pipeline_id ?? '');
      // Dwell stores the single stage in stage_id; change stores it in to_stage_id.
      setDealToStageId((tc.type === 'deal_stage_dwell' ? m.stage_id : m.to_stage_id) ?? '');
      setDealFromStageId(m.from_stage_id ?? '');
      if (typeof m.dwell_days === 'number') setDwellDays(m.dwell_days);
    } else if (tc.type === 'list_added') {
      setTriggerType('list_added');
      setListId(m.list_id ?? '');
    } else if (tc.type === 'new_placement') {
      setTriggerType('new_placement');
      setEmploymentType(placementOptionValue(m.employment_type));
    } else if (tc.type === 'meeting_booked') {
      setTriggerType('meeting_booked');
      setMeetingLinkIds(Array.isArray(m.event_type_ids) ? m.event_type_ids : []);
      setMeetingPersonType(m.person_type ?? 'any');
    } else {
      // application_stage_change / application_stage_dwell (and legacy
      // stage_change) map to the job option.
      setTriggerType('application_stage_change');
      setStageMode(tc.type === 'application_stage_dwell' ? 'dwell' : 'change');
      setJobId(m.job_id ?? '');
      // Campaigns saved before JT1-1917 hold capitalised stages; the options are JT's lowercase.
      setAppToStage(((tc.type === 'application_stage_dwell' ? m.stage : m.to_stage) ?? '').toLowerCase());
      setAppFromStage((m.from_stage ?? '').toLowerCase());
      if (typeof m.dwell_days === 'number') setDwellDays(m.dwell_days);
    }
  }, [campaign?.trigger_config]);

  const { info } = useMemo(() => ({ info: stepInfo('triggers', mode) }), [mode]);

  if (!id) return <Navigate to="/campaigns/new" replace />;
  if (isLoading) return <LoadingShell />;
  if (!campaign) return <Navigate to="/campaigns" replace />;
  if (campaign.status !== 'DRAFT') return <Navigate to={`/campaigns/${id}`} replace />;

  // Required fields per trigger so the auto-enrolment actually targets
  // something. Deal needs pipeline + target stage; job needs a target stage;
  // list needs the list id. new_placement filters are all optional.
  const dwellValid = stageMode !== 'dwell' || dwellDays >= 1;
  const triggerValid =
    mode === 'audience' ||
    (triggerType === 'application_stage_change' && appToStage.trim().length > 0 && dwellValid) ||
    (triggerType === 'deal_stage_change' && pipelineId.trim().length > 0 && dealToStageId.trim().length > 0 && dwellValid) ||
    (triggerType === 'list_added' && listId.trim().length > 0) ||
    triggerType === 'new_placement' ||
    triggerType === 'meeting_booked';

  const buildConfig = (): { type: string; match: Record<string, string | number | string[]> } => {
    const match: Record<string, string | number | string[]> = {};
    if (triggerType === 'application_stage_change') {
      if (jobId.trim()) match.job_id = jobId.trim();
      if (stageMode === 'dwell') {
        // application_stage_dwell: single stage + dwell_days.
        if (appToStage.trim()) match.stage = appToStage.trim();
        match.dwell_days = dwellDays;
        return { type: 'application_stage_dwell', match };
      }
      if (appToStage.trim()) match.to_stage = appToStage.trim();
      if (appFromStage.trim()) match.from_stage = appFromStage.trim();
      return { type: 'application_stage_change', match };
    } else if (triggerType === 'deal_stage_change') {
      if (pipelineId.trim()) match.pipeline_id = pipelineId.trim();
      if (stageMode === 'dwell') {
        // deal_stage_dwell: pipeline + single stage + dwell_days.
        if (dealToStageId.trim()) match.stage_id = dealToStageId.trim();
        match.dwell_days = dwellDays;
        return { type: 'deal_stage_dwell', match };
      }
      if (dealToStageId.trim()) match.to_stage_id = dealToStageId.trim();
      if (dealFromStageId.trim()) match.from_stage_id = dealFromStageId.trim();
      return { type: 'deal_stage_change', match };
    } else if (triggerType === 'list_added') {
      if (listId.trim()) match.list_id = listId.trim();
    } else if (triggerType === 'new_placement') {
      if (employmentType.trim()) match.employment_type = employmentType.trim();
    } else if (triggerType === 'meeting_booked') {
      if (meetingLinkIds.length) match.event_type_ids = meetingLinkIds;
      match.person_type = meetingPersonType;
    }
    return { type: triggerType, match };
  };

  const onNext = async () => {
    if (!triggerValid) return;
    try {
      if (mode === 'trigger') {
        // Trigger campaigns enrol via events, not a static audience — clear any
        // previously-selected audience so the two modes can't fight at launch.
        await updateCampaign.mutateAsync({ trigger_config: buildConfig() as TriggerConfig, audience_id: null });
        navigate(stepPath(id, 'sequence'));
      } else {
        await updateCampaign.mutateAsync({ trigger_config: null });
        navigate(stepPath(id, 'audience'));
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Could not save enrollment settings';
      addToast({ variant: 'danger', title: 'Save failed', description: msg });
    }
  };

  return (
    <WizardLayout step="triggers" campaignId={id} campaign={campaign} modeOverride={mode}>
      <FormCard>
        <FormHeader stepIndex={info.index} totalSteps={info.total} label="Enrollment" />

        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
          How should contacts enter this campaign? Pick a fixed audience to enrol everyone at launch, or set a
          trigger to auto-enrol contacts whenever a matching event happens in JobTetris.
        </p>

        <ModeCards mode={mode} onSelect={setMode} />

        {mode === 'trigger' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', paddingTop: 'var(--space-2)' }}>
            <Divider />
            <TriggerTypeSelector selected={triggerType} onSelect={setTriggerType} />

            {triggerType === 'application_stage_change' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <StageTimingToggle mode={stageMode} onMode={setStageMode} />
                {stageMode === 'dwell' && <DwellDaysField value={dwellDays} onChange={setDwellDays} />}
                <JobStageFields
                  dwell={stageMode === 'dwell'}
                  jobId={jobId}
                  onJob={setJobId}
                  toStage={appToStage}
                  onToStage={setAppToStage}
                  fromStage={appFromStage}
                  onFromStage={setAppFromStage}
                />
              </div>
            )}

            {triggerType === 'deal_stage_change' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <StageTimingToggle mode={stageMode} onMode={setStageMode} />
                {stageMode === 'dwell' && <DwellDaysField value={dwellDays} onChange={setDwellDays} />}
                <DealStageFields
                  dwell={stageMode === 'dwell'}
                  pipelineId={pipelineId}
                  onPipeline={(v) => {
                    setPipelineId(v);
                    // Stage ids are pipeline-scoped — reset stage picks when the
                    // pipeline changes so we never persist a mismatched pair.
                    setDealToStageId('');
                    setDealFromStageId('');
                  }}
                  toStageId={dealToStageId}
                  onToStageId={setDealToStageId}
                  fromStageId={dealFromStageId}
                  onFromStageId={setDealFromStageId}
                />
              </div>
            )}

            {triggerType === 'meeting_booked' && (
              <div
                style={{
                  padding: 'var(--space-4)',
                  borderRadius: 'var(--radius-input)',
                  border: '1px solid var(--color-border-default)',
                  backgroundColor: 'var(--color-surface-muted)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-secondary)',
                  lineHeight: 'var(--leading-relaxed)',
                }}
              >
                Enrols the person who books a meeting through a JobTetris booking link (matched by their email). The
                campaign captures the meeting&rsquo;s date/time, so a step can be scheduled relative to it (e.g. a
                reminder before the meeting).
              </div>
            )}

            {triggerType === 'meeting_booked' && (
              <MeetingBookedFields
                eventTypeIds={meetingLinkIds}
                personType={meetingPersonType}
                onChange={(next) => {
                  setMeetingLinkIds(next.event_type_ids);
                  setMeetingPersonType(next.person_type);
                }}
              />
            )}

            {triggerType === 'list_added' && (
              <SelectField
                label="List"
                required
                hint={
                  lists.length === 0
                    ? 'No JobTetris lists yet. They appear here a few minutes after Engage first syncs.'
                    : "Enrol someone when they're added to this JobTetris list."
                }
                value={listId}
                onChange={setListId}
                placeholder="Pick a list…"
                options={listOptions}
                disabled={lists.length === 0}
              />
            )}

            {triggerType === 'new_placement' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <SelectField
                  label="Placement type"
                  optional
                  hint="Only enrol on placements of this type."
                  value={employmentType}
                  onChange={setEmploymentType}
                  placeholder="Any type"
                  options={EMPLOYMENT_TYPE_OPTIONS}
                />
              </div>
            )}

            <TriggerNote />
          </div>
        )}

        <WizardFooter
          onBack={() => navigate(stepPath(id, 'basics'))}
          onNext={onNext}
          nextLabel={mode === 'trigger' ? 'Next: Sequence' : 'Next: Audience'}
          isPending={updateCampaign.isPending}
          primaryDisabled={!triggerValid}
        />
      </FormCard>
    </WizardLayout>
  );
}

/* ── Job pipeline stage fields ── */

function JobStageFields({
  dwell,
  jobId,
  onJob,
  toStage,
  onToStage,
  fromStage,
  onFromStage,
}: {
  dwell: boolean;
  jobId: string;
  onJob: (v: string) => void;
  toStage: string;
  onToStage: (v: string) => void;
  fromStage: string;
  onFromStage: (v: string) => void;
}) {
  const { data: jobsResp, isLoading: loadingJobs } = useJobs({ limit: 200 });
  const { data: stagesResp, isLoading: loadingStages } = useJobStages();
  const jobs = jobsResp?.data ?? [];
  const stages = stagesResp?.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <SelectField
        label="Job"
        optional
        hint={loadingJobs ? 'Loading jobs…' : 'Limit to one job, or leave as “Any job” to fire across all of them.'}
        value={jobId}
        onChange={onJob}
        placeholder="Any job"
        options={jobs.map((j) => ({ value: j.jt_job_id, label: j.title ?? j.jt_job_id }))}
      />
      <SelectField
        label={dwell ? 'In stage' : 'Moves into stage'}
        required
        hint={
          loadingStages
            ? 'Loading stages…'
            : dwell
              ? 'Enrol when an application has sat in this stage for the number of days above.'
              : 'Enrol when an application’s stage changes to this.'
        }
        value={toStage}
        onChange={onToStage}
        placeholder="Select a stage…"
        options={stages.map((s) => ({ value: s.value, label: s.label }))}
      />
      {!dwell && (
        <SelectField
          label="Coming from stage"
          optional
          hint="Leave as “Any stage” to enrol regardless of the previous stage."
          value={fromStage}
          onChange={onFromStage}
          placeholder="Any stage"
          options={stages.map((s) => ({ value: s.value, label: s.label }))}
        />
      )}
    </div>
  );
}

/* ── Deal pipeline stage fields ── */

function DealStageFields({
  dwell,
  pipelineId,
  onPipeline,
  toStageId,
  onToStageId,
  fromStageId,
  onFromStageId,
}: {
  dwell: boolean;
  pipelineId: string;
  onPipeline: (v: string) => void;
  toStageId: string;
  onToStageId: (v: string) => void;
  fromStageId: string;
  onFromStageId: (v: string) => void;
}) {
  const { data: pipelinesResp, isLoading } = usePipelines();
  const pipelines = pipelinesResp?.data ?? [];
  const selected = pipelines.find((p) => p.pipeline_id === pipelineId);
  const stageOptions = (selected?.stages ?? []).map((s) => ({ value: s.stage_id, label: s.name }));

  if (!isLoading && pipelines.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--space-4)',
          borderRadius: 'var(--radius-input)',
          border: '1px dashed var(--color-border-strong)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          color: 'var(--color-text-muted)',
          lineHeight: 'var(--leading-relaxed)',
        }}
      >
        No deal pipelines found yet. Run a JobTetris sync from Settings → JT sync, then come back. Your pipelines
        and their stages will appear here.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <SelectField
        label="Pipeline"
        required
        hint={isLoading ? 'Loading pipelines…' : 'Which deal pipeline this trigger watches.'}
        value={pipelineId}
        onChange={onPipeline}
        placeholder="Select a pipeline…"
        options={pipelines.map((p) => ({ value: p.pipeline_id, label: p.name }))}
      />
      <SelectField
        label={dwell ? 'In stage' : 'Moves into stage'}
        required
        hint={
          !pipelineId
            ? 'Pick a pipeline first.'
            : dwell
              ? 'Enrol the card’s contact(s) once it has sat in this stage for the number of days above.'
              : 'Enrol the card’s contact(s) when it moves into this stage.'
        }
        value={toStageId}
        onChange={onToStageId}
        placeholder={pipelineId ? 'Select a stage…' : 'Select a pipeline first'}
        options={stageOptions}
        disabled={!pipelineId}
      />
      {!dwell && (
        <SelectField
          label="Coming from stage"
          optional
          hint="Leave as “Any stage” to enrol regardless of the previous stage."
          value={fromStageId}
          onChange={onFromStageId}
          placeholder="Any stage"
          options={stageOptions}
          disabled={!pipelineId}
        />
      )}
    </div>
  );
}

/* ── Stage timing (move-in vs dwell) ── */

function StageTimingToggle({ mode, onMode }: { mode: 'change' | 'dwell'; onMode: (m: 'change' | 'dwell') => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <FieldLabel label="Trigger timing" required />
      <select
        value={mode}
        onChange={(e) => onMode(e.target.value as 'change' | 'dwell')}
        style={{ ...inputStyle(), cursor: 'pointer' }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = 'var(--input-border-focus)';
          e.currentTarget.style.boxShadow = 'var(--shadow-focus)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = 'var(--input-border)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <option value="change">When it moves into the stage</option>
        <option value="dwell">After it’s been in the stage for N days</option>
      </select>
    </div>
  );
}

function DwellDaysField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <FieldLabel label="Days in stage" required />
      <input
        type="number"
        min={1}
        max={365}
        value={value}
        onChange={(e) => onChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
        style={{ ...inputStyle(), width: '120px' }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = 'var(--input-border-focus)';
          e.currentTarget.style.boxShadow = 'var(--shadow-focus)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = 'var(--input-border)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      />
      <FieldHint text="Enrol once the card/application has been sitting in the chosen stage for at least this many days. Checked hourly." />
    </div>
  );
}

/* ── Mode selection cards ── */

function ModeCards({ mode, onSelect }: { mode: EnrollmentMode; onSelect: (m: EnrollmentMode) => void }) {
  const options: { id: EnrollmentMode; label: string; description: string; icon: ReactNode }[] = [
    {
      id: 'audience',
      label: 'Set by audience',
      description: 'Enrol everyone in a chosen audience or list at launch. Best for one-off outreach to a known group.',
      icon: <IconUsers />,
    },
    {
      id: 'trigger',
      label: 'Set by trigger',
      description: 'Auto-enrol contacts as events happen: stage changes, list adds, placements. Runs continuously while live.',
      icon: <IconBolt />,
    },
  ];
  return (
    <div role="radiogroup" aria-label="Enrollment method" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--space-3)' }}>
      {options.map((o) => {
        const isSelected = o.id === mode;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(o.id)}
            style={{
              textAlign: 'left',
              padding: 'var(--space-4)',
              borderRadius: 'var(--radius-input)',
              border: `1.5px solid ${isSelected ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
              backgroundColor: isSelected ? 'var(--color-blue-50)' : 'var(--color-surface-white)',
              boxShadow: isSelected ? 'var(--shadow-focus)' : 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
              fontFamily: 'var(--font-sans)',
              transition: 'border-color var(--transition-base), background-color var(--transition-base), box-shadow var(--transition-base)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'var(--space-9)',
                  height: 'var(--space-9)',
                  borderRadius: 'var(--radius-input)',
                  backgroundColor: isSelected ? 'var(--color-action-primary)' : 'var(--color-surface-muted)',
                  color: isSelected ? 'var(--color-action-primary-text)' : 'var(--color-text-secondary)',
                }}
              >
                {o.icon}
              </span>
              {isSelected && (
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                    borderRadius: 'var(--radius-full)',
                    backgroundColor: 'var(--color-action-primary)',
                    color: 'var(--color-action-primary-text)',
                  }}
                >
                  <IconCheck size={12} />
                </span>
              )}
            </div>
            <span style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: isSelected ? 'var(--color-text-brand)' : 'var(--color-text-primary)', letterSpacing: 'var(--tracking-tight)' }}>
              {o.label}
            </span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-snug)' }}>{o.description}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Trigger type selector ── */

function TriggerTypeSelector({ selected, onSelect }: { selected: UiTriggerKind; onSelect: (t: UiTriggerKind) => void }) {
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <legend
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-secondary)',
          padding: 0,
          marginBottom: 'var(--space-1)',
        }}
      >
        Trigger event
        <span aria-hidden="true" style={{ color: 'var(--color-text-danger)', marginLeft: 'var(--space-1)' }}>*</span>
      </legend>
      <div role="radiogroup" aria-label="Trigger event" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--space-3)' }}>
        {TRIGGER_TYPES.map((t) => {
          const isSelected = t.id === selected;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onSelect(t.id)}
              style={{
                textAlign: 'left',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-input)',
                border: `1.5px solid ${isSelected ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
                backgroundColor: isSelected ? 'var(--color-blue-50)' : 'var(--color-surface-white)',
                boxShadow: isSelected ? 'var(--shadow-focus)' : 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                fontFamily: 'var(--font-sans)',
                transition: 'border-color var(--transition-base), background-color var(--transition-base), box-shadow var(--transition-base)',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'var(--space-9)',
                  height: 'var(--space-9)',
                  borderRadius: 'var(--radius-input)',
                  backgroundColor: isSelected ? 'var(--color-action-primary)' : 'var(--color-surface-muted)',
                  color: isSelected ? 'var(--color-action-primary-text)' : 'var(--color-text-secondary)',
                }}
              >
                {t.icon}
              </span>
              <span style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: isSelected ? 'var(--color-text-brand)' : 'var(--color-text-primary)', letterSpacing: 'var(--tracking-tight)' }}>
                {t.label}
              </span>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-snug)' }}>{t.description}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/* ── Inputs ── */

function SelectField({
  label,
  required,
  optional,
  hint,
  value,
  onChange,
  placeholder,
  options,
  disabled,
}: {
  label: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <FieldLabel label={label} required={required} optional={optional} />
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle(), cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = 'var(--input-border-focus)';
          e.currentTarget.style.boxShadow = 'var(--shadow-focus)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = 'var(--input-border)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <option value="">{placeholder ?? 'Select…'}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && <FieldHint text={hint} />}
    </div>
  );
}

function FieldLabel({ label, required, optional }: { label: string; required?: boolean; optional?: boolean }) {
  return (
    <label
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--color-text-secondary)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
      }}
    >
      {label}
      {required && <span aria-hidden="true" style={{ color: 'var(--color-text-danger)' }}>*</span>}
      {optional && <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-regular)', color: 'var(--color-text-subtle)', marginLeft: 'var(--space-1)' }}>· optional</span>}
    </label>
  );
}

function FieldHint({ text }: { text: string }) {
  return <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-normal)' }}>{text}</p>;
}

function TriggerNote() {
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-input)',
        backgroundColor: 'var(--color-surface-muted)',
        border: '1px solid var(--color-border-default)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        color: 'var(--color-text-muted)',
        lineHeight: 'var(--leading-relaxed)',
      }}
    >
      Trigger campaigns don't use a fixed audience. Contacts are enrolled automatically as matching events
      fire while the campaign is live. Each contact is enrolled once. You'll build the message sequence next.
    </div>
  );
}

function Divider() {
  return <div style={{ height: '1px', backgroundColor: 'var(--color-border-default)' }} />;
}

function LoadingShell() {
  return (
    <WizardLayout step="triggers">
      <FormCard>
        <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text-muted)' }}>Loading campaign…</p>
      </FormCard>
    </WizardLayout>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    display: 'block',
    width: '100%',
    height: 'var(--input-height)',
    padding: 'var(--input-padding-y) var(--input-padding-x)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--input-radius)',
    backgroundColor: 'var(--input-bg)',
    fontSize: 'var(--input-font-size)',
    fontFamily: 'var(--font-sans)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
  };
}

/* ── Icons ── */

function IconCheck({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>; }
function IconUsers({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>; }
function IconBolt({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>; }
function IconFlow({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="6" height="6" rx="1" /><rect x="15" y="15" width="6" height="6" rx="1" /><path d="M6 9v3a3 3 0 0 0 3 3h6" /></svg>; }
function IconColumns({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="6" height="18" rx="1" /><rect x="9" y="3" width="6" height="12" rx="1" /><rect x="15" y="3" width="6" height="8" rx="1" /></svg>; }
function IconList({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>; }
function IconBriefcase({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>; }
function IconCalendar({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>; }
